import type { ProjectPublicQuery, ProjectPublicQueryVariables, ProjectQuery, ProjectQueryVariables } from '~/shared/api'
import { ProjectDocument, ProjectMemberRoleEnum, ProjectPublicDocument } from '~/shared/api'
import { isUuid, resolveProjectQueryVariables } from '~/shared/lib'

export interface ProjectAccessContext {
  admins: string[]
  ownerId: string
}

export interface ResolvedProjectRoute {
  projectRef: string
  projectId: string
  projectSlug: string | null
  accessContext: ProjectAccessContext
}

const EMPTY_PROJECT_ACCESS_CONTEXT: ProjectAccessContext = {
  admins: [],
  ownerId: '',
}

export interface FetchProjectByRouteRefOptions {
  usePublicQuery?: boolean
}

function extractProjectAccessContext(
  project: NonNullable<ProjectQuery['project']>,
): ProjectAccessContext {
  return {
    admins: project.projectMembers.nodes
      .filter(member => member.role === ProjectMemberRoleEnum.Admin)
      .map(member => member.userId),
    ownerId: project.ownerUserId,
  }
}

async function fetchPublicProjectByRouteRef(
  client: NonNullable<ReturnType<typeof getApolloClient>>,
  variables: ProjectPublicQueryVariables,
  trimmedRef: string,
): Promise<ResolvedProjectRoute | null> {
  const { data } = await client.query({
    query: ProjectPublicDocument,
    variables,
    fetchPolicy: 'cache-first',
  })

  const publicProject = data?.publicProject
  if (!publicProject?.id) {
    return null
  }

  return {
    projectRef: trimmedRef,
    projectId: publicProject.id,
    projectSlug: publicProject.slug?.trim() || null,
    accessContext: EMPTY_PROJECT_ACCESS_CONTEXT,
  }
}

function getApolloClient() {
  const nuxtApp = useNuxtApp()
  const apollo = nuxtApp.$apollo as {
    defaultClient?: { query: (options: unknown) => Promise<{ data?: ProjectQuery | ProjectPublicQuery }> }
  } | undefined

  return apollo?.defaultClient ?? null
}

export async function fetchProjectByRouteRef(
  ref: string,
  options?: FetchProjectByRouteRefOptions,
): Promise<ResolvedProjectRoute | null> {
  const trimmedRef = ref.trim()
  if (!trimmedRef) {
    return null
  }

  const client = getApolloClient()
  if (!client) {
    return null
  }

  const variables = resolveProjectQueryVariables(trimmedRef) as ProjectQueryVariables

  if (options?.usePublicQuery) {
    try {
      return await fetchPublicProjectByRouteRef(
        client,
        variables as ProjectPublicQueryVariables,
        trimmedRef,
      )
    }
    catch {
      return null
    }
  }

  try {
    const { data } = await client.query({
      query: ProjectDocument,
      variables,
      fetchPolicy: 'cache-first',
    })

    const project = data?.project
    if (!project?.id) {
      return null
    }

    return {
      projectRef: trimmedRef,
      projectId: project.id,
      projectSlug: project.slug?.trim() || null,
      accessContext: extractProjectAccessContext(project),
    }
  }
  catch {
    return null
  }
}

export async function resolveProjectIdFromRouteRef(ref: string): Promise<string> {
  if (isUuid(ref)) {
    return ref
  }

  const resolved = await fetchProjectByRouteRef(ref)
  return resolved?.projectId ?? ref
}
